// Approved analytics taxonomy sourced from src/utils/analytics/events/*.csv

export type AnalyticsEnvironment = 'production' | 'staging' | 'development';

export type PageName =
  | 'signup'
  | 'email_verification'
  | 'terms_and_conditions'
  | 'get_started'
  | 'home'
  | 'workgroup_details'
  | 'design_schema'
  | 'load_data'
  | 'query_editor'
  | 'explore_graph'
  | 'marketplace_solutions'
  | 'marketplace_add_ons'
  | 'admin_users'
  | 'admin_audit_log'
  | 'billing'
  | 'settings'
  | 'create_workgroup'
  | 'create_workspace';

export type JourneyName = 'account_creation' | 'onboarding' | 'product_usage' | 'data_ingestion';

export type OnboardingJourneyStep = 'data_upload' | 'schema_review' | 'build_graph' | 'graph_results';

export type JourneyStep =
  | 'signup'
  | 'email_verification'
  | 'legal_acceptance'
  | 'provisioning'
  | OnboardingJourneyStep
  | 'exploration_choice'
  | 'platform_entry'
  | 'exploration'
  | 'select_source'
  | 'configure_source'
  | 'configure_mapping'
  | 'confirm'
  | 'loading_jobs';

export type EntryContext =
  | 'direct_navigation'
  | 'onboarding_preloaded_solution'
  | 'onboarding_own_data'
  | 'workspace_navigation'
  | 'deep_link'
  | 'refresh';

export type DataSourceTypeValue =
  | 'local_file'
  | 'amazon_s3'
  | 'google_cloud_storage'
  | 'azure_blob_storage'
  | 'snowflake'
  | 'delta_lake'
  | 'iceberg'
  | 'spark'
  | 'kafka'
  | 'postgres'
  | 'bigquery';

export type OrganizationRole = 'organization_admin' | 'member' | 'viewer';

export type SignupMethod = 'username_password' | 'google_sso';

export type EmailDomainType = 'corporate' | 'public' | 'unknown';

export type WorkspaceProfileStatus =
  | 'provisioning'
  | 'running'
  | 'stopped'
  | 'suspended'
  | 'failed'
  | 'terminating'
  | 'terminated';

// Internal workgroup-details tab keys → approved page_viewed tab_selected taxonomy.
const WORKGROUP_DETAILS_TAB_SELECTED: Record<string, string> = {
  general: 'general',
  workspaces: 'workspace',
  databases: 'database',
  monitor: 'monitor',
  solution: 'solution',
  dataprofiles: 'data_profile',
  access_management: 'access_management',
  backup_and_restore: 'backup_and_restore',
  network_access: 'network_access',
};

const BILLING_TAB_SELECTED: Record<string, string> = {
  // The Billing (customer portal) tab replaced Overview as the default landing tab.
  Billing: 'overview',
  Overview: 'overview',
  PaymentMethods: 'payment_methods',
  Invoices: 'invoices',
  PrepaidCredits: 'credits',
};

const SETTINGS_TAB_SELECTED: Record<string, string> = {
  org_settings: 'organization_setting',
  api_key: 'api_keys',
};

const QUERY_EDITOR_TAB_SELECTED: Record<string, string> = {
  graphs: 'graphs',
  files: 'files',
};

// tools-models DataSourceType values -> approved data_source_type controlled values.
const DATA_SOURCE_TYPE: Record<string, DataSourceTypeValue> = {
  file: 'local_file',
  s3: 'amazon_s3',
  gcs: 'google_cloud_storage',
  abs: 'azure_blob_storage',
  snowflake: 'snowflake',
  deltaLake: 'delta_lake',
  iceberg: 'iceberg',
  spark: 'spark',
  kafka: 'kafka',
  postgres: 'postgres',
  bq: 'bigquery',
};

// Load Data wizard step -> approved journey_step. The wizard's own step names are the
// breadcrumb labels; CONFIGURE_FILE is the step the taxonomy calls configure_source.
const LOAD_DATA_JOURNEY_STEP: Record<number, JourneyStep> = {
  0: 'select_source',
  1: 'configure_source',
  2: 'configure_mapping',
  3: 'confirm',
};

export function resolveAnalyticsEnvironment(): AnalyticsEnvironment {
  const mode = import.meta.env.MODE;
  if (mode === 'production') {
    return 'production';
  }
  if (mode === 'staging' || mode === 'uat') {
    return 'staging';
  }
  return 'development';
}

// tab_selected is a controlled vocabulary. An unmapped key must resolve to null rather
// than leak the raw internal key or silently masquerade as a different tab: a wrong
// value corrupts reporting quietly, a null one shows up as an obvious gap.
function toTabSelected(mapping: Record<string, string>, tabKey: string, surface: string): string | null {
  const mapped = mapping[tabKey];
  if (mapped) {
    return mapped;
  }
  if (tabKey && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[analytics] unmapped ${surface} tab "${tabKey}" — add it to the approved tab_selected values`);
  }
  return null;
}

export function toWorkgroupDetailsTabSelected(tabKey: string): string | null {
  return toTabSelected(WORKGROUP_DETAILS_TAB_SELECTED, tabKey, 'workgroup_details');
}

export function toBillingTabSelected(tabKey: string): string | null {
  return toTabSelected(BILLING_TAB_SELECTED, tabKey, 'billing');
}

export function toSettingsTabSelected(tabKey: string): string | null {
  return toTabSelected(SETTINGS_TAB_SELECTED, tabKey, 'settings');
}

export function toQueryEditorTabSelected(tabKey: string): string | null {
  return toTabSelected(QUERY_EDITOR_TAB_SELECTED, tabKey, 'query_editor');
}

/**
 * data_source_type is a controlled vocabulary that must never carry a raw connector key.
 * An unmapped source resolves to undefined so the property is omitted rather than wrong.
 */
export function toDataSourceType(type: string | undefined): DataSourceTypeValue | undefined {
  if (!type) {
    return undefined;
  }
  const mapped = DATA_SOURCE_TYPE[type];
  if (!mapped && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[analytics] unmapped data source type "${type}" — add it to the approved data_source_type values`);
  }
  return mapped;
}

/**
 * The Load Data page shows the loading-job list until the wizard drawer is opened over it,
 * so the visible stage is the wizard step when it is open and loading_jobs when it is not.
 */
export function toLoadDataJourneyStep(isWizardOpen: boolean, stepIndex: number): JourneyStep {
  if (!isWizardOpen) {
    return 'loading_jobs';
  }
  return LOAD_DATA_JOURNEY_STEP[stepIndex] ?? 'select_source';
}

export function mapWorkspaceStatusForProfile(status?: string): WorkspaceProfileStatus | undefined {
  if (!status) {
    return undefined;
  }

  switch (status) {
    case 'Pending':
    case 'Updating':
    case 'Upgrading':
    case 'Resizing':
    case 'Resuming':
    case 'Stopping':
    case 'Unknown':
      return 'provisioning';
    case 'Active':
    case 'Degraded':
      return 'running';
    case 'Idle':
    case 'Stopped':
      return 'stopped';
    case 'Pausing':
    case 'Paused':
      return 'suspended';
    case 'Error':
      return 'failed';
    case 'Terminating':
      return 'terminating';
    case 'Terminated':
      return 'terminated';
    default:
      return undefined;
  }
}

export function sanitizePagePath(pathname: string): string | undefined {
  if (!pathname || pathname === '/') {
    return pathname || undefined;
  }
  // Strip query/hash are already absent on pathname; keep only the path segment.
  return pathname;
}
